/*
 * New product-card quick-add. "+" trigger fetches the product via the Section
 * Rendering API (`?section_id=quick-add-product`) into the shared drawer.
 * Mobile: bottom sheet + toast. Desktop: right drawer + cart drawer.
 */
(function () {
  "use strict";

  var DESKTOP_MQ = "(min-width: 990px)";
  var TOAST_DISMISS_MS = 9000;
  var viewCache = {};

  // Mirrors the PDP variant picker block, read from [data-variant-picker-config] on a rendered product page.
  var LOW_STOCK_FADE_MS = 150;
  var pickerConfig = null;

  // Set on the drawer to match the PDP, see component-variant-size-options.css
  var PICKER_STYLE_CLASSES = [
    "variant-picker--text",
    "variant-picker--radio",
    "variant-picker--button",
    "variant-buttons--gap",
  ];

  function readPickerConfig(el) {
    return {
      buttons_style: el.dataset.buttonsStyle,
      button_layout_style: el.dataset.buttonLayout,
      show_low_stock: el.dataset.showLowStock === "true",
      low_stock_threshold: parseInt(el.dataset.lowStockThreshold, 10) || 0,
    };
  }

  function getPickerConfig() {
    return pickerConfig;
  }

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function lowStockEnabled(cfg) {
    return !!cfg && cfg.show_low_stock === true && cfg.buttons_style === "button";
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function getCart() {
    return document.querySelector("cart-drawer") || document.querySelector("cart-notification");
  }

  // cart-drawer.js can't swap the header bubble (its selector doesn't exist in
  // the section HTML), so update it here.
  function updateHeaderCartBubble(state) {
    var sectionHtml = state.sections && state.sections["cart-icon-bubble"];
    var target = document.getElementById("cart-icon-bubble");
    if (!sectionHtml || !target) return;
    var doc = new DOMParser().parseFromString(sectionHtml, "text/html");
    var fresh = doc.querySelector(".shopify-section") || doc.body;
    target.innerHTML = fresh.innerHTML;
  }

  /* ----------------------------- Quick-add drawer ----------------------------- */
  class QuickAddDrawerNew extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector("[data-qad-overlay]");
      this.panel = this.querySelector(".qad__panel");
      this.content = this.querySelector("[data-qad-content]");
      this.loader = this.querySelector("[data-qad-loading]");
      this.swiper = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener("click", this.onClick);
      this.addEventListener("change", this.onChange);
    }

    /* ------------------------------- open/close ------------------------------- */
    async open(url) {
      this.setAttribute("aria-hidden", "false");
      this.classList.add("qad--active");
      document.body.classList.add("overflow-hidden");
      document.addEventListener("keydown", this.onKeydown);
      await this.load(url);
    }

    close() {
      this.classList.remove("qad--active", "qad--open", "qad--sizeguide-open");
      this.setAttribute("aria-hidden", "true");
      document.body.classList.remove("overflow-hidden");
      document.removeEventListener("keydown", this.onKeydown);
      this.destroySwiper();
    }

    onKeydown(e) {
      if (e.key !== "Escape") return;
      if (this.classList.contains("qad--sizeguide-open")) this.closeSizeGuide();
      else this.close();
    }

    /* --------------------------------- fetch --------------------------------- */
    async load(url) {
      // Clear immediately so the previous product never flashes.
      this.destroySwiper();
      this.content.innerHTML = "";
      this.classList.remove("qad--sizeguide-open");
      this.showLoader(true);
      try {
        var results = await Promise.all([this.fetchView(url), this.fetchPickerConfig(url)]);
        this.render(results[0]);
      } catch (err) {
        console.error("[quick-add] failed to load", url, err);
        this.close();
      } finally {
        this.showLoader(false);
      }
    }

    async fetchView(url) {
      if (viewCache[url]) return viewCache[url];
      var fetchUrl = url + (url.indexOf("?") === -1 ? "?" : "&") + "section_id=quick-add-product";
      var res = await fetch(fetchUrl);
      if (!res.ok) throw new Error("HTTP " + res.status);
      var text = await res.text();
      var doc = new DOMParser().parseFromString(text, "text/html");
      var qa = doc.querySelector(".qa");
      if (!qa) throw new Error("no .qa in response");
      viewCache[url] = qa.outerHTML;
      return viewCache[url];
    }

    // On failure drawer keeps the default style.
    async fetchPickerConfig(url) {
      if (getPickerConfig() && !isDesignMode()) return;
      var config = {};
      try {
        var local = document.querySelector("[data-variant-picker-config]");
        if (local) {
          config = readPickerConfig(local);
        } else {
          var res = await fetch(url);
          if (res.ok) {
            var doc = new DOMParser().parseFromString(await res.text(), "text/html");
            var el = doc.querySelector("[data-variant-picker-config]");
            if (el) config = readPickerConfig(el);
          }
        }
      } catch (err) {
        console.warn("[quick-add] could not mirror the PDP variant picker", err);
      }
      pickerConfig = config;
    }

    // Drawer renders PDP markup, mirroring style is carrying same class PDP uses.
    applyPickerConfig() {
      var cfg = getPickerConfig();
      var size = this.content.querySelector("[data-qa-size]");
      if (!cfg || !size) return;

      if (cfg.buttons_style) {
        PICKER_STYLE_CLASSES.forEach(function (cls) {
          size.classList.remove(cls);
        });
        size.classList.add("variant-picker--" + cfg.buttons_style);
        if (cfg.buttons_style === "button" && cfg.button_layout_style === "style_2") {
          size.classList.add("variant-buttons--gap");
        }
      }

      if (!lowStockEnabled(cfg)) return;
      size.querySelectorAll("[data-qa-inventory]").forEach(function (input) {
        var qty = parseInt(input.dataset.qaInventory, 10);
        if (qty > 0 && qty <= cfg.low_stock_threshold) input.classList.add("qa__size--low");
      });
    }

    render(html) {
      this.classList.remove("qad--sizeguide-open");
      this.destroySwiper();
      this.content.innerHTML = html;
      this.applyPickerConfig();
      this.panel.scrollTop = 0;
      this.initSwiper();
      // Slide the panel in first now, so the animation runs at full panel height
      // (sliding while empty = no visible movement on mobile).
      void this.panel.offsetHeight;
      if (this.classList.contains("qad--active")) this.classList.add("qad--open");
      this.panel.focus();
    }

    showLoader(on) {
      if (this.loader) this.loader.classList.toggle("is-active", !!on);
    }

    /* --------------------------------- swiper -------------------------------- */
    initSwiper() {
      var el = this.content.querySelector(".qa__swiper");
      if (!el || typeof window.Swiper === "undefined") return;
      var slides = el.querySelectorAll(".swiper-slide").length;
      if (isDesktop()) {
        this.initDesktopScroll(el, slides);
        return;
      }
      // Single image: static 1-column image (no slider).
      if (slides < 2) return;
      // Mobile: 2-up horizontal slider with dots.
      this.swiper = new window.Swiper(el, {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 0,
        speed: 300,
        pagination: {
          el: el.parentElement.querySelector(".qa__pagination"),
          clickable: true,
        },
      });
    }

    // Desktop: smooth eased scrolling (wheel + cursor drag with momentum) and a
    // progress thumb that tracks scroll position (0 at top, end at bottom).
    initDesktopScroll(el, slides) {
      if (slides < 2) {
        el.style.cursor = "default";
        return;
      }
      var pagination = el.parentElement.querySelector(".qa__pagination");
      var thumb = null;
      var heightPct = 100 / slides;
      if (pagination && slides >= 2) {
        pagination.innerHTML = '<span class="qa__progress-fill"></span>';
        thumb = pagination.firstElementChild;
        thumb.style.height = heightPct + "%";
      }

      function updateThumb() {
        if (!thumb) return;
        var max = el.scrollHeight - el.clientHeight;
        var progress = max > 0 ? Math.min(Math.max(el.scrollTop / max, 0), 1) : 0;
        thumb.style.top = progress * (100 - heightPct) + "%";
      }

      var target = el.scrollTop;
      var raf = null;
      function maxScroll() { return el.scrollHeight - el.clientHeight; }
      function clamp(v) { return Math.max(0, Math.min(maxScroll(), v)); }
      function tick() {
        if (!el.isConnected) { raf = null; return; }
        var diff = target - el.scrollTop;
        if (Math.abs(diff) < 0.5) {
          el.scrollTop = target;
          raf = null;
        } else {
          el.scrollTop += diff * 0.14;
          raf = requestAnimationFrame(tick);
        }
        updateThumb();
      }
      function goTo(v) {
        target = clamp(v);
        if (!raf) raf = requestAnimationFrame(tick);
      }

      el.addEventListener("wheel", function (e) {
        if (maxScroll() <= 0) return;
        e.preventDefault();
        goTo(target + e.deltaY);
      }, { passive: false });

      // Cursor drag with momentum
      var dragging = false, startY = 0, startScroll = 0, lastY = 0, lastT = 0, velocity = 0;
      el.addEventListener("pointerdown", function (e) {
        if (e.pointerType !== "mouse" || e.button !== 0) return;
        if (e.target.closest("wk-button, button, a")) return;
        dragging = true;
        startY = lastY = e.clientY;
        startScroll = el.scrollTop;
        lastT = performance.now();
        velocity = 0;
        target = el.scrollTop;
        if (raf) { cancelAnimationFrame(raf); raf = null; }
        el.classList.add("is-dragging");
        el.setPointerCapture(e.pointerId);
      });
      el.addEventListener("pointermove", function (e) {
        if (!dragging) return;
        var now = performance.now();
        velocity = (e.clientY - lastY) / Math.max(now - lastT, 1);
        lastY = e.clientY;
        lastT = now;
        target = clamp(startScroll - (e.clientY - startY));
        el.scrollTop = target;
        updateThumb();
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        el.classList.remove("is-dragging");
        goTo(target - velocity * 160);
      }
      el.addEventListener("pointerup", endDrag);
      el.addEventListener("pointercancel", endDrag);
      el.addEventListener("dragstart", function (e) { e.preventDefault(); });

      el.addEventListener("scroll", updateThumb, { passive: true });
      updateThumb();
    }

    destroySwiper() {
      if (this.swiper && this.swiper.destroy) {
        try { this.swiper.destroy(true, true); } catch (e) {}
      }
      this.swiper = null;
    }

    /* ------------------------------ interactions ----------------------------- */
    onClick(e) {
      // Close (overlay, close buttons)
      if (e.target.closest("[data-qad-overlay]") || e.target.closest("[data-qad-close]")) {
        e.preventDefault();
        this.close();
        return;
      }
      // Size guide open/close
      if (e.target.closest("[data-qa-sizeguide-open]")) {
        e.preventDefault();
        this.openSizeGuide();
        return;
      }
      if (e.target.closest("[data-qa-sizeguide-close]")) {
        e.preventDefault();
        this.closeSizeGuide();
        return;
      }
      // Sibling color swatch → load that product into the same drawer
      var swatchLink = e.target.closest(".qa__color product-swatch a, .qa__color a.swatch-circle, .qa__color a.swatch-link, .qa__color a.swatch-btn");
      if (swatchLink && swatchLink.getAttribute("href")) {
        e.preventDefault();
        var href = swatchLink.getAttribute("href").split("?")[0];
        this.load(href);
        return;
      }
      // Add to cart
      var atc = e.target.closest("[data-qa-atc]");
      if (atc) {
        e.preventDefault();
        this.addToCart(atc);
        return;
      }
    }

    // Sold out sizes are disabled radios, so they never get here.
    onChange(e) {
      var input = e.target.closest("[data-qa-size-input]");
      if (input) this.selectSize(input);
    }

    selectSize(input) {
      var qa = this.content.querySelector(".qa");
      var selectedLabel = qa.querySelector("[data-qa-size-selected]");
      if (selectedLabel) selectedLabel.textContent = ": " + (input.dataset.variantTitle || input.value);

      var atc = qa.querySelector("[data-qa-atc]");
      var atcText = qa.querySelector("[data-qa-atc-text]");
      atc.dataset.variantId = input.dataset.variantId;
      atc.disabled = false;
      if (atcText) atcText.textContent = window.quickAddStrings.addToCart;

      this.updateLowStock(input);
    }

    // Show count for last 1-2, else generic "few left"
    // Fade and height fade in/out match the PDP
    updateLowStock(input) {
      var el = this.content.querySelector("[data-qa-low-stock]");
      if (!el) return;

      var text = "";
      if (input.classList.contains("qa__size--low")) {
        var qty = parseInt(input.dataset.qaInventory, 10);
        text =
          qty <= 2
            ? window.quickAddStrings.lowStockCount.replace("[qty]", qty)
            : window.quickAddStrings.lowStockFew;
      }

      var showing = el.lowStockNext === undefined ? el.textContent.trim() : el.lowStockNext;
      if (showing === text) return;
      el.lowStockNext = text;
      clearTimeout(el.lowStockTimer);

      var fill = function () {
        el.innerHTML = "";
        if (!text) return;
        var marker = document.createElement("span");
        marker.className = "qa__low-stock-marker";
        marker.setAttribute("aria-hidden", "true");
        el.appendChild(marker);
        el.appendChild(document.createTextNode(text));
      };

      if (!text) {
        el.classList.add("qa__low-stock--hidden");
        el.lowStockTimer = setTimeout(function () {
          fill();
          el.classList.add("qa__low-stock--collapsed");
        }, LOW_STOCK_FADE_MS);
        return;
      }

      if (showing === "") {
        fill();
        el.classList.remove("qa__low-stock--collapsed");
        el.lowStockTimer = setTimeout(function () {
          el.classList.remove("qa__low-stock--hidden");
        }, LOW_STOCK_FADE_MS);
        return;
      }

      el.classList.add("qa__low-stock--hidden");
      el.lowStockTimer = setTimeout(function () {
        fill();
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            el.classList.remove("qa__low-stock--hidden");
          });
        });
      }, LOW_STOCK_FADE_MS);
    }

    openSizeGuide() {
      this.classList.add("qad--sizeguide-open");
      var sg = this.content.querySelector("[data-qa-sizeguide]");
      if (sg) sg.hidden = false;
    }

    closeSizeGuide() {
      this.classList.remove("qad--sizeguide-open");
    }

    /* -------------------------------- add to cart ---------------------------- */
    async addToCart(atc) {
      var variantId = atc.dataset.variantId;
      if (!variantId || atc.disabled) return;

      var qa = this.content.querySelector(".qa");
      var cart = getCart();
      atc.classList.add("loading");
      var spinner = atc.querySelector(".loading-overlay__spinner");
      if (spinner) spinner.classList.remove("hidden");
      atc.disabled = true;

      var config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      var formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);
      if (cart && cart.getSectionsToRender) {
        formData.append("sections", cart.getSectionsToRender().map(function (s) { return s.section; }));
        formData.append("sections_url", window.location.pathname);
      }
      config.body = formData;

      try {
        var res = await fetch(routes.cart_add_url, config);
        var state = await res.json();
        if (state.status) {
          console.error("[quick-add] add to cart error", state.description);
          return;
        }

        if (typeof publish !== "undefined" && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "quick-add-new", productVariantId: variantId });
        }

        updateHeaderCartBubble(state);

        if (isDesktop() && cart && cart.renderContents) {
          // Desktop: open the cart drawer with the new item (also refreshes the header count).
          this.close();
          cart.renderContents(state);
        } else {
          // Mobile: refresh cart sections without opening the drawer, then show the toast.
          if (cart && cart.renderContents) {
            cart.setAttribute("disabled", "");
            cart.renderContents(state);
            cart.removeAttribute("disabled");
          }
          var toast = document.querySelector("added-to-cart-toast");
          this.close();
          if (toast) toast.show(qa, atc);
        }
      } catch (err) {
        console.error("[quick-add] add to cart failed", err);
      } finally {
        atc.classList.remove("loading");
        if (spinner) spinner.classList.add("hidden");
        atc.disabled = false;
      }
    }
  }

  /* ------------------------------ Added-to-cart toast ----------------------------- */
  class AddedToCartToast extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector("[data-act-overlay]");
      this.itemSlot = this.querySelector("[data-act-item]");
      this.timer = null;
      this.onClick = this.onClick.bind(this);
      this.addEventListener("click", this.onClick);
    }

    onClick(e) {
      if (e.target.closest("[data-act-overlay]") || e.target.closest("[data-act-close]")) {
        // Let the checkout link follow its href; everything else closes.
        if (!e.target.closest(".act__checkout")) {
          e.preventDefault();
          this.hide();
        }
      }
    }

    show(qa, atc) {
      this.itemSlot.innerHTML = this.buildItem(qa, atc);
      this.setAttribute("aria-hidden", "false");
      this.classList.add("act--active");
      clearTimeout(this.timer);
      var self = this;
      this.timer = setTimeout(function () { self.hide(); }, TOAST_DISMISS_MS);
    }

    hide() {
      clearTimeout(this.timer);
      this.classList.remove("act--active");
      this.setAttribute("aria-hidden", "true");
    }

    buildItem(qa, atc) {
      var img = "";
      var activeSlideImg = qa.querySelector(".swiper-slide-active .qa__img") || qa.querySelector(".qa__img");
      if (activeSlideImg) img = activeSlideImg.currentSrc || activeSlideImg.src;

      var titleEl = qa.querySelector(".qa__title");
      var title = titleEl ? titleEl.textContent.trim() : "";

      var priceEl = qa.querySelector(".qa__price .price__regular .price-item, .qa__price .price-item--regular, .qa__price .price-item");
      var price = priceEl ? priceEl.textContent.trim() : (qa.querySelector(".qa__price") ? qa.querySelector(".qa__price").textContent.trim() : "");

      // Color name from the swatches title, falling back to the active swatch.
      var color = "";
      var colorTitleEl = qa.querySelector(".qa__color .product-swatches-title");
      if (colorTitleEl) {
        color = colorTitleEl.textContent.replace(/\s+/g, " ").trim();
        if (window.quickAddStrings.color) color = color.replace(window.quickAddStrings.color, "").trim();
      }
      if (!color) {
        var activeSwatch = qa.querySelector(".qa__color .swatch-circle.active, .qa__color product-swatch a.active");
        color = activeSwatch ? (activeSwatch.dataset.swatchName || "") : "";
      }

      var selectedSize = qa.querySelector("[data-qa-size-input]:checked") || atc;
      var size = selectedSize && selectedSize.dataset ? (selectedSize.dataset.variantTitle || "") : "";

      var options = "";
      if (color) options += '<div class="product-option"><dt>' + window.quickAddStrings.color + "</dt><dd>" + color + "</dd></div>";
      if (size && size.toLowerCase() !== "default title") {
        options += '<div class="product-option"><dt>' + window.quickAddStrings.size + ":</dt><dd>" + size + "</dd></div>";
      }

      return (
        '<div class="cart-item act-cart-item">' +
          '<div class="cart-item__media">' +
            (img ? '<img class="cart-item__image" src="' + img + '" alt="" width="120" loading="lazy">' : "") +
          "</div>" +
          '<div class="cart-item__info">' +
            '<div class="cart-item__details">' +
              '<div class="cart-item-header-wrapper">' +
                '<span class="cart-item__name h4">' + title + "</span>" +
              "</div>" +
              (options ? '<dl class="product-options-wrapper">' + options + "</dl>" : "") +
              (price ? '<div class="cart-item__price-wrapper"><span class="price">' + price + "</span></div>" : "") +
            "</div>" +
          "</div>" +
        "</div>"
      );
    }
  }

  /* --------------------------------- bootstrap -------------------------------- */
  function defineEls() {
    if (!customElements.get("quick-add-drawer-new")) customElements.define("quick-add-drawer-new", QuickAddDrawerNew);
    if (!customElements.get("added-to-cart-toast")) customElements.define("added-to-cart-toast", AddedToCartToast);
  }

  // Delegated: cards get re-rendered by sliders.
  document.addEventListener("click", function (e) {
    var trigger = e.target.closest("[data-quick-add-url]");
    if (!trigger) return;
    e.preventDefault();
    var drawer = document.querySelector("quick-add-drawer-new");
    if (drawer) drawer.open(trigger.getAttribute("data-quick-add-url"));
  });

  defineEls();
})();
